import { Star } from 'lucide-react';

// ── Size system —— badge diameter is ~37-40% of the avatar it sits on ─────────
//   xs  →  13 px  ( typeahead   32 px avatar  ~40% )
//   sm  →  18 px  ( feed/search 40-64 px      ~35% )
//   md  →  23 px  ( explore/profile 80-96 px  ~27% )
export type StarBadgeSize = 'xs' | 'sm' | 'md';

interface SizeConfig {
    badge:  number; // badge circle diameter (px)
    icon:   number; // star icon size (px)
    offset: number; // how far outside the corner (px)
    border: number; // outline width (px)
}

const SIZES: Record<StarBadgeSize, SizeConfig> = {
    xs: { badge: 13, icon:  7, offset: 4, border: 1.5 },
    sm: { badge: 18, icon: 10, offset: 5, border: 2.0 },
    md: { badge: 23, icon: 13, offset: 6, border: 2.0 },
};

// ── Per-color config ── mirrors page-header.tsx STAR_COLORS exactly ────────────
// Star = vivid icon color.  Background = very light tint of that color.
// Glow ring = same vivid color, sonar-ping expands+fades.
interface ColorConfig {
    star:     string;  // vivid icon color (the star itself)
    bg:       string;  // soft background gradient (light tint)
    ring:     string;  // sonar-ring rgba color
    shadow:   string;  // colored box-shadow glow
    outline:  string;  // badge border color
    filter:   string;  // drop-shadow on the star icon
}

const STAR_COLORS: Record<number, ColorConfig> = {
    // 1 — Gold / Amber  (text-amber-400 / bg-amber-400/20)
    1: {
        star:    '#FBBF24',
        bg:      'radial-gradient(circle at 35% 30%, rgba(255,251,235,0.97), rgba(254,243,199,0.90))',
        ring:    'rgba(251,191, 36,0.55)',
        shadow:  '0 0 9px rgba(245,158,11,0.65), 0 2px 4px rgba(0,0,0,0.20)',
        outline: 'rgba(251,191,36,0.75)',
        filter:  'drop-shadow(0 0 3px rgba(245,158,11,0.90)) drop-shadow(0 1px 1px rgba(0,0,0,0.25))',
    },
    // 2 — Blue  (text-blue-500 / bg-blue-500/20)
    2: {
        star:    '#3B82F6',
        bg:      'radial-gradient(circle at 35% 30%, rgba(239,246,255,0.97), rgba(219,234,254,0.90))',
        ring:    'rgba( 59,130,246,0.55)',
        shadow:  '0 0 9px rgba(59,130,246,0.65), 0 2px 4px rgba(0,0,0,0.20)',
        outline: 'rgba(96,165,250,0.75)',
        filter:  'drop-shadow(0 0 3px rgba(59,130,246,0.90)) drop-shadow(0 1px 1px rgba(0,0,0,0.25))',
    },
    // 3 — Purple  (text-purple-500 / bg-purple-500/20)
    3: {
        star:    '#A855F7',
        bg:      'radial-gradient(circle at 35% 30%, rgba(250,245,255,0.97), rgba(243,232,255,0.90))',
        ring:    'rgba(168, 85,247,0.55)',
        shadow:  '0 0 9px rgba(168,85,247,0.65), 0 2px 4px rgba(0,0,0,0.20)',
        outline: 'rgba(192,132,252,0.75)',
        filter:  'drop-shadow(0 0 3px rgba(168,85,247,0.90)) drop-shadow(0 1px 1px rgba(0,0,0,0.25))',
    },
    // 4 — Green  (text-green-500 / bg-green-500/20)
    4: {
        star:    '#22C55E',
        bg:      'radial-gradient(circle at 35% 30%, rgba(240,253,244,0.97), rgba(220,252,231,0.90))',
        ring:    'rgba( 34,197, 94,0.55)',
        shadow:  '0 0 9px rgba(34,197,94,0.65), 0 2px 4px rgba(0,0,0,0.20)',
        outline: 'rgba(74,222,128,0.75)',
        filter:  'drop-shadow(0 0 3px rgba(34,197,94,0.90)) drop-shadow(0 1px 1px rgba(0,0,0,0.25))',
    },
    // 5 — Red  (text-red-500 / bg-red-500/20)
    5: {
        star:    '#EF4444',
        bg:      'radial-gradient(circle at 35% 30%, rgba(255,241,241,0.97), rgba(254,226,226,0.90))',
        ring:    'rgba(239, 68, 68,0.55)',
        shadow:  '0 0 9px rgba(239,68,68,0.65), 0 2px 4px rgba(0,0,0,0.20)',
        outline: 'rgba(248,113,113,0.75)',
        filter:  'drop-shadow(0 0 3px rgba(239,68,68,0.90)) drop-shadow(0 1px 1px rgba(0,0,0,0.25))',
    },
    // 6 — Pink  (text-pink-500 / bg-pink-500/20)
    6: {
        star:    '#EC4899',
        bg:      'radial-gradient(circle at 35% 30%, rgba(253,242,248,0.97), rgba(252,231,243,0.90))',
        ring:    'rgba(236, 72,153,0.55)',
        shadow:  '0 0 9px rgba(236,72,153,0.65), 0 2px 4px rgba(0,0,0,0.20)',
        outline: 'rgba(244,114,182,0.75)',
        filter:  'drop-shadow(0 0 3px rgba(236,72,153,0.90)) drop-shadow(0 1px 1px rgba(0,0,0,0.25))',
    },
    // 7 — Orange  (text-orange-500 / bg-orange-500/20)
    7: {
        star:    '#F97316',
        bg:      'radial-gradient(circle at 35% 30%, rgba(255,247,237,0.97), rgba(255,237,213,0.90))',
        ring:    'rgba(249,115, 22,0.55)',
        shadow:  '0 0 9px rgba(249,115,22,0.65), 0 2px 4px rgba(0,0,0,0.20)',
        outline: 'rgba(251,146,60,0.75)',
        filter:  'drop-shadow(0 0 3px rgba(249,115,22,0.90)) drop-shadow(0 1px 1px rgba(0,0,0,0.25))',
    },
};

interface StarBadgeProps {
    starColor: number | null | undefined;
    size?: StarBadgeSize;
    className?: string;
}

/**
 * Animated, size-proportional star badge for memorial page avatars.
 *
 * The star icon shows in its actual vivid color (gold, blue, purple …)
 * against a very light tint background — matching the page-header.tsx style.
 *
 * Place inside a `relative`-positioned wrapper that is NOT `overflow-hidden`.
 * The badge extends outside the avatar corner via negative offset.
 *
 * Size guide:
 *   xs → typeahead / very small avatars  (≤ 32 px)
 *   sm → feed / search cards             (40–64 px)  [default]
 *   md → explore / profile large cards   (80–96 px)
 */
export function StarBadge({ starColor, size = 'sm', className = '' }: StarBadgeProps) {
    if (!starColor || starColor <= 0) return null;

    const cfg = STAR_COLORS[starColor] ?? STAR_COLORS[1];
    const s   = SIZES[size];

    return (
        <div
            className={`absolute z-20 pointer-events-none ${className}`}
            style={{
                top:    `-${s.offset}px`,
                right:  `-${s.offset}px`,
                width:  `${s.badge}px`,
                height: `${s.badge}px`,
            }}
        >
            {/* Sonar-ping ring — expands outward and fades */}
            <div
                className="star-badge-ring"
                style={{ backgroundColor: cfg.ring }}
            />

            {/* Badge body — gentle breathe pulse */}
            <div
                className="star-badge-body"
                style={{
                    width:      `${s.badge}px`,
                    height:     `${s.badge}px`,
                    background: cfg.bg,
                    border:     `${s.border}px solid ${cfg.outline}`,
                    boxShadow:  cfg.shadow,
                }}
            >
                <Star
                    style={{
                        width:      `${s.icon}px`,
                        height:     `${s.icon}px`,
                        fill:       cfg.star,
                        color:      cfg.star,
                        filter:     cfg.filter,
                        flexShrink: 0,
                    }}
                />
            </div>
        </div>
    );
}
